import { useEffect, useRef } from 'react';

/**
 * 通知系ポーリングを1本の共通タイマーにまとめるための仕組み。
 *
 * プッシュ通知（Web Push）を「メインのリアルタイム経路」に位置づけ、
 * ここでのポーリングは「プッシュが届かない環境・ブラウザ通知を閉じた後などの
 * 保険（フォールバック）」として、間隔を長めに設定する方針。
 *
 * - タスクごとに間隔（interval）を指定でき、頻度の低いデータは間隔を伸ばせる
 * - 画面が非表示のときは全タスクをまとめてスキップ
 * - 前回の取得が未完了のタスクはスキップ（リクエストの積み上げ防止）
 * - タブ復帰・フォーカス時、および Service Worker がプッシュ受信時に
 *   送ってくるメッセージで全タスクを即時リフレッシュし、ポーリング待ちなしで
 *   ベルを最新化する
 */

/** 通知系の「保険」ポーリング間隔（15秒）。未読カウントなど軽い取得向け */
export const POLL_FALLBACK = 15000;

/** 一覧・アクティビティなど重めの取得の保険ポーリング間隔（30秒） */
export const POLL_FALLBACK_SLOW = 30000;

/**
 * 開いているスレッド（ITサポート／相談）の即時反映用ポーリング間隔（1秒）。
 * ただし「差分取得（前回以降の新着だけ）」と必ずセットで使うこと。
 * 全件を取り直すと通信量・DB負荷が跳ね上がるため、差分取得の前提でのみ短縮する。
 */
export const POLL_THREAD = 1000;

interface PollTask {
  fn: () => void | Promise<void>;
  interval: number;
  lastRun: number;
  inFlight: boolean;
}

const tasks = new Set<PollTask>();
let timerId: number | null = null;
let lastForcedRun = 0;

function runTick(force = false) {
  if (document.visibilityState !== 'visible') return;
  const now = Date.now();
  tasks.forEach((task) => {
    if (task.inFlight) return;
    if (!force && now - task.lastRun < task.interval) return;
    task.lastRun = now;
    task.inFlight = true;
    Promise.resolve()
      .then(() => task.fn())
      .catch(() => {
        // エラーは各フック側で処理済み。ここで握りつぶしてタイマーを止めない。
      })
      .finally(() => {
        task.inFlight = false;
      });
  });
}

function ensureTicker() {
  if (timerId !== null) return;
  timerId = window.setInterval(() => runTick(), 1000);

  // タブ復帰・ウィンドウフォーカス時に即時リフレッシュする。
  // プッシュ通知を受けてアプリへ戻ってきた場合、ポーリング待ちせずに
  // ベルを最新化するため（visibilitychange と focus の連続発火は 3 秒で間引く）。
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastForcedRun < 3000) return;
    lastForcedRun = now;
    runTick(true);
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);

  // Service Worker（プッシュ受信時）からのメッセージを拾い、即時リフレッシュする。
  // タブが表示中のままでも、プッシュ受信の瞬間にポーリング待ちせずベルを最新化する。
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'PUSH_RECEIVED') {
        runTick(true);
      }
    });
  }
}

/**
 * ポーリングタスクを登録し、解除用関数を返す。
 * useEffect 内で呼び、クリーンアップで返り値を実行して解除する。
 */
export function registerPolling(
  fn: () => void | Promise<void>,
  interval = POLL_FALLBACK
): () => void {
  ensureTicker();
  // 同時刻に多数のタスクが発火する「バースト」を避けるため、
  // 初回発火タイミングを interval 内の乱数ぶんずらす。
  // 以降も各タスクが自分の interval で回るため、ずれは維持される。
  const jitter = Math.random() * interval;
  const task: PollTask = {
    fn,
    interval,
    lastRun: Date.now() - interval + jitter,
    inFlight: false,
  };
  tasks.add(task);
  return () => {
    tasks.delete(task);
  };
}

/**
 * React フック版。fn は最新のものを ref で保持するため、
 * 呼び出し側で useCallback に包まなくても再登録されない。
 */
export function usePolling(fn: () => void | Promise<void>, interval = POLL_FALLBACK) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    return registerPolling(() => fnRef.current(), interval);
  }, [interval]);
}